<template>
    <HeaderLayout>

        <div v-if="isShowSlider">
            <CandidateSlider :id="currentContest.id" class="mt-5" />
        </div>
        <div v-else class="scoring-container">
            <!-- Header Section -->
            <div class="scoring-header">
                <div class="header-content">
                    <h1 class="pageant-title">{{ currentContest.name || 'Judges Scoring' }}</h1>
                    <div class="judge-info">
                        <i class="bi bi-person-badge"></i>
                        <span>Judge Panel</span>
                    </div>
                </div>
            </div>

            <!-- Scoring Card -->
            <div class="scoring-card">
                <div class="card-body">
                    <!-- Contestants Table -->
                    <div class="table-container">
                        <div class="table-responsive">
                            <table class="judges-scoring-table">
                                <thead>
                                    <tr>
                                        <th scope="col" class="contestant-number">#</th>
                                        <th scope="col" class="contestant-photo">Photo</th>
                                        <th scope="col" class="contestant-name">Contestant Name</th>
                                        <th scope="col" class="contestant-agency">Agency</th>
                                        <th scope="col"
                                            v-for="(criteria) in (scoreCards[0]?.judgeScores[0]?.scores || [])"
                                            :key="criteria.criteriaId" class="criteria-column">
                                            <div class="criteria-header">
                                                <span class="criteria-name">{{ criteria.criterionName }}</span>
                                                <span class="criteria-weight">{{ criteria.weight }}%</span>
                                            </div>
                                        </th>
                                        <th scope="col" class="total-score">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(scoreCard) in scoreCards" :key="scoreCard.contestantId"
                                        class="contestant-row">
                                        <td class="contestant-number fw-bold">{{ scoreCard.userPK }}</td>
                                        <td class="contestant-photo">
                                            <div class="profile-image-container">
                                                <img :src="`${apiConfig.baseUrl}${scoreCard.profilePic}`"
                                                    alt="Profile Picture" class="profile-image"
                                                    @error="handleImageError" />
                                                <div class="image-overlay"></div>
                                            </div>
                                        </td>
                                        <td class="contestant-name">
                                            <span class="name-text">{{ scoreCard.contestantName }}</span>
                                        </td>
                                        <td class="contestant-agency">
                                            <span class="agency-text">{{ scoreCard.contestantAgency }}</span>
                                        </td>
                                        <td v-for="(score) in (scoreCard?.judgeScores[0]?.scores || [])"
                                            :key="score.scoreId" class="score-cell">
                                            <div class="score-input-container">
                                                <input class="score-input" type="text" max="10" step="0.1"
                                                    @keypress="validateNumericInput" @input="validateScore(score)"
                                                    v-model.number="score.scoreValue" @blur="formatScore(score)"
                                                    :class="{ 'score-highlight': score.scoreValue > 0 }" />
                                                <div class="score-border"></div>
                                            </div>
                                        </td>
                                        <td class="total-score">
                                            <div class="total-display">
                                                <span class="total-value">{{ scoreCard.totalScore }}</span>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- Action Buttons -->
                    <div class="action-section">
                        <button @click="saveScores" class="save-btn" type="button">
                            <i class="bi bi-save"></i>
                            <span>Save Scores</span>
                        </button>
                    </div>

                    <!-- Footer -->
                    <div class="scoring-footer">
                        <div class="powered-by">
                            <div class="logo-container">
                                <img src="/favicon.ico" alt="Exodous Logo" class="company-logo" />
                            </div>
                            <div class="company-info">
                                <span class="powered-text">Powered By</span>
                                <span class="company-name">Exodous I.T. Solutions</span>
                                <span class="system-name">Tabulation System</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <BroadcastModal :show-broadcast-modal="showBroadcastModal"
            :broadcast-timestamp="currentMessage?.broadcastTimestamp"
            :broadcast-priority="currentMessage?.broadcastPriority" @dismiss="toggleBroadcastModal"
            :broadcast-message="currentMessage?.message" />
    </HeaderLayout>
</template>


<script setup>
import dashboardService from "@/services/dashboardService";
import spinnerService from "@/services/spinnerService";
import { onMounted, onUnmounted, ref, watch } from "vue"
import signalRService from "@/services/signalRService";
import toastService from "@/services/toastService";
import CandidateSlider from "@/components/CandidateSlider.vue";
import { apiConfig } from "@/utils/config";
import { useStore } from "vuex";
import HeaderLayout from "@/layouts/HeaderLayout.vue";
import BroadcastModal from "@/components/modals/BroadcastModal.vue";

const store = useStore();
const isShowSlider = ref(true);
const currentContest = ref({ name: '' });
const scoreCards = ref([{ judgeScores: [] }]);
const showBroadcastModal = ref(false);
const currentMessage = ref({ broadcastPriority: 'normal', message: '' });
const currentContestId = ref('');

const toggleBroadcastModal = () => {
    showBroadcastModal.value = !showBroadcastModal.value;
}
// Handle image loading errors
const handleImageError = (event) => {
    event.target.src = '/female-place-holder.jpg'; // Fallback image
};

const fetchData = async () => {
    spinnerService.show();
    try {
        const resp = await dashboardService.getCurrentScores();
        scoreCards.value = resp.scoreCards;
        currentContest.value = resp.contest;
        if (currentContest.value.isSlider === true) {
            store.dispatch("setCurrentEventTitle", "EXODOUS TABULATION SYSTEM");
            isShowSlider.value = true;
        } else {
            store.dispatch("setCurrentEventTitle", currentContest.value.name);
            isShowSlider.value = false;
        }
    } catch (error) {
        console.error('Error fetching scores:', error);
        toastService.error('Failed to load scoring data');
    } finally {
        spinnerService.hide();
    }
};

const validateScore = (score) => {
    if (score.scoreValue < 0 || score.scoreValue > 10) {
        toastService.error("Score must be between 0 and 10.");
        score.scoreValue = Math.min(Math.max(score.scoreValue, 0), 10);
    }
};

const formatScore = (score) => {
    const value = score.scoreValue?.toString().trim();

    if (!value || isNaN(value)) {
        score.scoreValue = "0";
    } else {
        score.scoreValue = parseFloat(value);
    }
};
const autoSaveInterval = 30000;
const hasUnsavedScores = ref(false);
let autoSaveTimeout = null;

const autoSave = async () => {
    if (currentContest.value && currentContest.value.isOpen && !isShowSlider.value && hasUnsavedScores.value) {
        await dashboardService.saveScores(scoreCards.value);
        hasUnsavedScores.value = false; // Reset after saving
    }
    else
        console.log('nothing to save');

    // Schedule next check
    autoSaveTimeout = setTimeout(() => {
        autoSave();
    }, autoSaveInterval);
};
watch(
    () => {
        const safeScoreCards = scoreCards.value || [];
        return safeScoreCards.map(card => {
            const safeJudgeScores = card?.judgeScores || [];
            return safeJudgeScores.map(judge => {
                const safeScores = judge?.scores || [];
                return safeScores.map(score => score?.scoreValue ?? 0);
            });
        });
    },
    (newScores, oldScores) => {
        newScores; oldScores;
        // Only trigger if we're still on the same contest
        if (currentContest.value?.id === currentContestId.value) {
            hasUnsavedScores.value = true;
            console.log('Score values changed within same contest...');
        }
    },
    { deep: true }
);

// When contest changes, update the tracked ID and reset unsaved flag
watch(() => currentContest.value?.id, (newContestId) => {
    currentContestId.value = newContestId;
    hasUnsavedScores.value = false; // Reset when contest changes
    console.log('Contest changed, resetting unsaved flag');
});

// Start the auto-save loop

const validateNumericInput = (event) => {
    const char = event.key;
    const allowedChars = "0123456789.";
    const currentValue = event.target.value;

    // Prevent multiple decimal points
    if (char === "." && currentValue.includes(".")) {
        event.preventDefault();
    }

    // Allow only numeric characters and a single decimal point
    if (!allowedChars.includes(char)) {
        event.preventDefault();
    }
};

const handleStatus = (evnt) => {
    if (currentContest.value.id === evnt.id) {
        currentContest.value.isOpen = evnt.status;
    }
};

const saveScores = async () => {
    spinnerService.show();
    try {
        const response = await dashboardService.saveScores(scoreCards.value);
        if (response.success) {
            scoreCards.value = response.scoreCards;
            hasUnsavedScores.value = false;
            toastService.success("Scores saved successfully!");

            // Add visual feedback
            document.querySelectorAll('.score-input').forEach(input => {
                if (input.value > 0) {
                    input.classList.add('score-update');
                    setTimeout(() => {
                        input.classList.remove('score-update');
                    }, 300);
                }
            });
        } else {
            toastService.error(response.message);
        }
    } catch (error) {
        console.error('Error saving scores:', error);
        toastService.error('Failed to save scores');
    } finally {
        spinnerService.hide();
    }
};

const handleContestChanged = async (payload) => {
    try {
        signalRService.connection.invoke("ConfirmDelivery", payload.eventId); //inform server that we received the event so it wont send again
        //Make sure there will be no data loss if their are unsaved changes when the event was fired.
        if (currentContest.value && currentContest.value.isOpen && !isShowSlider.value && hasUnsavedScores.value) {
            await dashboardService.saveScores(scoreCards.value);
            hasUnsavedScores.value = false; // Reset after saving
        }
    }
    catch {
        //
    }
    finally {
        fetchData();
    }
};

const handleBroadcastMessage = (payload) => {
    console.log('broadcast received...', payload);
    currentMessage.value = payload;
    toggleBroadcastModal();
    setTimeout(async () => {
        await signalRService.connection.invoke("ConfirmDelivery", payload.eventId);
    }, 100);
}

const initilizeBroadcastListener = () => {
    if (!signalRService.connection) {
        setTimeout(() => {
            initilizeBroadcastListener();
        }, 500);
    }
    else {
        console.log('connection initialized...');
        signalRService.subscribeToEvent("BroadcastMessageReceived", handleBroadcastMessage)
        signalRService.subscribeToEvent("OnCurrentContestChanged", handleContestChanged);
        signalRService.subscribeToEvent("OnContestStatusChanged", (evnt) => handleStatus(evnt));
    }
}

onMounted(() => {
    initilizeBroadcastListener();
    fetchData();
    autoSave();
});
onUnmounted(() => {
    if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
    }
})
</script>

<style scoped>
/* Main Container */
.scoring-container {
    min-height: 100vh;
    background: linear-gradient(135deg, var(--dark-bg) 0%, #1a1a1a 100%);
    padding: 20px;
    font-family: 'Poppins', sans-serif;
}

/* Header Section */
.scoring-header {
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(10px);
    border-radius: 15px;
    padding: 20px;
    margin-bottom: 20px;
    border: 1px solid var(--border-gold);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 15px;
}

.pageant-title {
    font-size: 2rem;
    font-weight: 700;
    background: linear-gradient(135deg, var(--text-gold) 0%, var(--gold-light) 50%, var(--gold-primary) 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    text-shadow: 0 0 20px rgba(212, 175, 55, 0.5);
    margin: 0;
}

.judge-info {
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--text-light);
    background: rgba(212, 175, 55, 0.1);
    padding: 10px 15px;
    border-radius: 25px;
    border: 1px solid var(--border-gold);
}

.judge-info i {
    color: var(--gold-primary);
    font-size: 1.2rem;
}

/* Scoring Card */
.scoring-card {
    background: var(--card-bg);
    border-radius: 15px;
    border: 1px solid var(--border-gold);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    overflow: hidden;
}

.card-body {
    padding: 0;
}

/* Table Container */
.table-container {
    overflow-x: auto;
    padding: 20px;
}

.judges-scoring-table {
    width: 100%;
    border-collapse: collapse;
    background: var(--darker-bg);
    border-radius: 10px;
    overflow: hidden;
}

/* Table Head */
.judges-scoring-table thead {
    background: linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(184, 148, 31, 0.1) 100%);
    border-bottom: 2px solid var(--border-gold);
}

.judges-scoring-table th {
    padding: 15px 10px;
    color: var(--text-gold);
    font-weight: 600;
    text-align: center;
    border-right: 1px solid var(--border-gold);
}

.judges-scoring-table th:last-child {
    border-right: none;
}

.criteria-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
}

.criteria-name {
    font-size: 0.9rem;
    font-weight: 600;
}

.criteria-weight {
    font-size: 0.75rem;
    color: var(--gold-light);
    background: rgba(212, 175, 55, 0.2);
    padding: 2px 8px;
    border-radius: 10px;
}

/* Table Body */
.judges-scoring-table tbody tr {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    transition: all 0.3s ease;
}

.judges-scoring-table tbody tr:hover {
    background: rgba(212, 175, 55, 0.05);
}

.judges-scoring-table td {
    padding: 15px 10px;
    text-align: center;
    vertical-align: middle;
    border-right: 1px solid rgba(255, 255, 255, 0.05);
}

.judges-scoring-table td:last-child {
    border-right: none;
}

/* Contestant Number */
.contestant-number {
    color: var(--gold-primary);
    font-size: 1.1rem;
    min-width: 50px;
}

/* Profile Photo */
.contestant-photo {
    min-width: 80px;
}

.profile-image-container {
    position: relative;
    width: 60px;
    height: 60px;
    margin: 0 auto;
    border-radius: 50%;
    overflow: hidden;
    border: 2px solid var(--border-gold);
    box-shadow: 0 0 15px var(--shadow-gold);
}

.profile-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
}

.profile-image-container:hover .profile-image {
    transform: scale(1.1);
}

.image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, transparent 0%, rgba(212, 175, 55, 0.1) 100%);
}

/* Contestant Name & Agency */
.contestant-name {
    min-width: 150px;
    text-align: left !important;
}

.name-text {
    color: var(--text-gold);
    font-weight: 600;
    font-size: 1rem;
}

.contestant-agency {
    min-width: 120px;
    text-align: left !important;
}

.agency-text {
    color: var(--text-light);
    font-size: 0.9rem;
}

/* Score Input */
.score-cell {
    min-width: 100px;
}

.score-input-container {
    position: relative;
    display: inline-block;
}

.score-input {
    width: 80px;
    height: 45px;
    text-align: center;
    font-weight: 700;
    font-size: 1rem;
    background: rgba(0, 0, 0, 0.3);
    border: 2px solid transparent;
    border-radius: 8px;
    color: var(--text-light);
    transition: all 0.3s ease;
    outline: none;
    position: relative;
    z-index: 2;
}

.score-input:focus {
    border-color: var(--gold-primary);
    background: rgba(0, 0, 0, 0.5);
    box-shadow: 0 0 15px var(--shadow-gold);
    color: var(--text-gold);
}

.score-highlight {
    background: rgba(212, 175, 55, 0.1);
    border-color: var(--gold-primary);
    color: var(--text-gold);
}

.score-border {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 8px;
    background: linear-gradient(135deg, var(--gold-primary) 0%, var(--gold-secondary) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 1;
}

.score-input:focus+.score-border {
    opacity: 0.3;
}

/* Total Score */
.total-score {
    min-width: 100px;
}

.total-display {
    background: linear-gradient(135deg, var(--gold-primary) 0%, var(--gold-secondary) 100%);
    border-radius: 8px;
    padding: 8px 12px;
    margin: 0 auto;
    max-width: 90px;
}

.total-value {
    color: var(--dark-bg);
    font-weight: 700;
    font-size: 1.1rem;
}

/* Action Section */
.action-section {
    padding: 20px;
    border-top: 1px solid var(--border-gold);
    background: rgba(0, 0, 0, 0.3);
}

.save-btn {
    background: linear-gradient(135deg, var(--gold-primary) 0%, var(--gold-secondary) 100%);
    color: var(--dark-bg);
    border: none;
    padding: 12px 30px;
    border-radius: 25px;
    font-weight: 600;
    font-size: 1rem;
    display: flex;
    align-items: center;
    gap: 10px;
    margin-left: auto;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(212, 175, 55, 0.3);
}

.save-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(212, 175, 55, 0.5);
}

.save-btn:active {
    transform: translateY(0);
}

/* Footer */
.scoring-footer {
    padding: 20px;
    border-top: 1px solid var(--border-gold);
    background: rgba(0, 0, 0, 0.5);
}

.powered-by {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 15px;
    flex-wrap: wrap;
}

.logo-container {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    overflow: hidden;
    border: 1px solid var(--border-gold);
    box-shadow: 0 0 10px var(--shadow-gold);
}

.company-logo {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.company-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
}

.powered-text {
    font-size: 0.8rem;
    color: var(--text-muted);
}

.company-name {
    font-weight: 600;
    color: var(--text-light);
    font-size: 0.9rem;
}

.system-name {
    font-size: 0.8rem;
    color: var(--gold-primary);
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
    .scoring-container {
        padding: 10px;
    }

    .header-content {
        flex-direction: column;
        text-align: center;
    }

    .pageant-title {
        font-size: 1.5rem;
    }

    .table-container {
        padding: 10px;
    }

    .judges-scoring-table {
        font-size: 0.85rem;
    }

    .contestant-number {
        min-width: 40px;
        font-size: 1rem;
    }

    .contestant-photo {
        min-width: 60px;
    }

    .profile-image-container {
        width: 45px;
        height: 45px;
    }

    .contestant-name,
    .contestant-agency {
        min-width: 120px;
    }

    .score-cell {
        min-width: 80px;
    }

    .score-input {
        width: 65px;
        height: 40px;
        font-size: 0.9rem;
    }

    .total-score {
        min-width: 80px;
    }

    .total-display {
        padding: 6px 8px;
        max-width: 70px;
    }

    .total-value {
        font-size: 0.9rem;
    }

    .criteria-name {
        font-size: 0.8rem;
    }

    .criteria-weight {
        font-size: 0.7rem;
    }
}

@media (max-width: 480px) {
    .pageant-title {
        font-size: 1.3rem;
    }

    .judges-scoring-table {
        font-size: 0.8rem;
    }

    .contestant-name,
    .contestant-agency {
        min-width: 100px;
    }

    .score-input {
        width: 55px;
        height: 35px;
        font-size: 0.85rem;
    }

    .save-btn {
        width: 100%;
        justify-content: center;
    }
}

/* Animation for score updates */
@keyframes scoreUpdate {
    0% {
        transform: scale(1);
    }

    50% {
        transform: scale(1.05);
    }

    100% {
        transform: scale(1);
    }
}

.score-update {
    animation: scoreUpdate 0.3s ease;
}
</style>
